<template>
  <div class="tarjetas-grid">
    <div
      v-for="p in products"
      :key="p.id"
      class="product-card"
      :class="{ 'product-card--in-order': qty(p.id) > 0 }"
    >
      <!-- In-order badge -->
      <div v-if="qty(p.id) > 0" class="product-card__badge">
        En pedido · {{ qty(p.id) }}
      </div>

      <!-- SKU + unit -->
      <div class="product-card__sku">SKU {{ p.sku }} · {{ p.unit }}</div>

      <!-- Name -->
      <div class="product-card__name">{{ p.name }}</div>

      <!-- Price + qty -->
      <div class="product-card__footer">
        <div>
          <div class="product-card__price">{{ money(p.price) }}</div>
          <div class="product-card__unit-label">por {{ p.unit.toLowerCase() }}</div>
        </div>
        <div class="product-card__input-row">
          <span
            v-if="lastQty(p.id)"
            :title="`Último pedido: ${lastQty(p.id)}`"
            class="hint-badge"
          >{{ lastQty(p.id) }}</span>
          <span v-else class="hint-placeholder" />
          <QtyInput
            :modelValue="qty(p.id)"
            :decimal="p.decimal"
            @update:modelValue="v => $emit('set-qty', { comedorId: focusedComedor, productId: p.id, qty: v })"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, watch } from 'vue'
import QtyInput from '../ui/QtyInput.vue'

function money(n) { return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }

const props = defineProps({
  products:       { type: Array,  required: true },
  focusedComedor: { type: [String, Number], default: null },
  cart:           { type: Object, required: true },
  store:          { type: Object, default: null },
})

defineEmits(['set-qty'])

function qty(productId) {
  return props.cart[props.focusedComedor]?.[productId] || 0
}

function lastQty(productId) {
  return props.store?.lastOrderItems?.[props.focusedComedor]?.[String(productId)] || 0
}

function loadHints() {
  if (props.focusedComedor != null) {
    props.store?.loadLastOrderItems?.([props.focusedComedor])
  }
}

onMounted(loadHints)
watch(() => props.focusedComedor, loadHints)
</script>

<style scoped>
/* ─── Grid ───────────────────────────────────────────────────────────────────── */
.tarjetas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
  padding: 24px;
}

/* ─── Card ───────────────────────────────────────────────────────────────────── */
.product-card {
  padding: 16px 18px;
  background: var(--surface);
  border-radius: 8px;
  border: 1.5px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  transition: border-color 0.12s;
}

.product-card--in-order {
  border-color: var(--primary);
}

/* ─── Badge ──────────────────────────────────────────────────────────────────── */
.product-card__badge {
  position: absolute;
  top: -7px;
  right: 14px;
  padding: 2px 8px;
  background: var(--primary);
  color: #fff;
  font-family: var(--ff-head);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  border-radius: 4px;
  text-transform: uppercase;
}

/* ─── Content ────────────────────────────────────────────────────────────────── */
.product-card__sku {
  font-family: var(--ff-body);
  font-size: 10px;
  color: var(--text-3);
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.product-card__name {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 14px;
  color: var(--text);
  line-height: 1.35;
  min-height: 38px;
}

/* ─── Card footer ────────────────────────────────────────────────────────────── */
.product-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
}

.product-card__price {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 20px;
  color: var(--text);
  letter-spacing: -0.3px;
}

.product-card__unit-label {
  font-family: var(--ff-body);
  font-size: 11px;
  color: var(--text-3);
}

.product-card__input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ─── Last-order hint badge ──────────────────────────────────────────────────── */
.hint-badge {
  font-family: var(--ff-head);
  font-size: 14px;
  font-weight: 700;
  color: var(--hint);
  background: rgba(249, 115, 22, 0.08);
  border: 1.5px solid rgba(249, 115, 22, 0.35);
  border-radius: 4px;
  padding: 1px 5px;
  line-height: 1.4;
  flex-shrink: 0;
  white-space: nowrap;
}

.hint-placeholder {
  width: 28px;
  flex-shrink: 0;
}
</style>
